import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '../utils/api';

/** Manual products/suppléments saisis à la finalisation ; stockés dans appointment.pricing.workflowSupplements */
export interface WorkflowSupplement {
  id: string;
  productId?: string;
  productCode?: string;
  piercingTypeId?: string;
  name: string;
  price: number;
  qty: number;
}

function normalizeWorkflowSupplements(raw: unknown): WorkflowSupplement[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x: any, i: number) => ({
      id: String(x?.id ?? `supp-${i}`),
      productId: x?.productId ? String(x.productId) : undefined,
      productCode: x?.productCode ? String(x.productCode) : undefined,
      piercingTypeId: x?.piercingTypeId ? String(x.piercingTypeId) : undefined,
      name: String(x?.name ?? '').trim(),
      price: Math.max(0, Number(x?.price) || 0),
      qty: Math.max(1, Math.floor(Number(x?.qty)) || 1),
    }))
    .filter((s) => s.name.length > 0);
}

function supplementsSignature(list: WorkflowSupplement[]): string {
  return JSON.stringify(list.map((s) => ({
    id: s.id,
    productId: s.productId,
    productCode: s.productCode,
    piercingTypeId: s.piercingTypeId,
    name: s.name,
    price: s.price,
    qty: s.qty,
  })));
}

export interface PiercingAct {
  id: string;
  sequence: number;
  status: string;
  demandDetails: {
    bodyZoneId?: string;
    piercingTypeModelId?: string;
    type?: string;
    placement?: Record<string, any>;
    jewelry?: Record<string, any>;
    notes?: string;
    isMinor?: boolean;
    guardianFirstName?: string;
    guardianLastName?: string;
    /** Persisté côté serveur quand le praticien valide « acte terminé » sur l’étape photos. */
    photosCaptureConfirmed?: boolean;
  } | null;
  piercingRecord?: {
    id: string;
    placement?: any;
    jewelry?: any;
    needleGauge?: string;
    aftercareProvided?: any;
    photos?: any;
    bodyZone?: { id: string; name: string };
    piercingTypeModel?: { id: string; name: string; basePrice?: number };
  } | null;
  service?: { id: string; name: string; category: string; basePrice?: number };
  contracts?: Array<{
    id: string;
    status: string;
    signedAt?: string;
    /** Stored signature entries keyed by role (practitioner, client, legalGuardian) */
    signatures?: Record<string, { dataUrl?: string; signerName?: string; signerRole?: string; signedAt?: string }>;
    /** Legacy/minor path: guardian image sometimes persisted only on this column */
    legalGuardian?: { dataUrl?: string; signedAt?: string; signerName?: string } | null;
    templateVersion?: string;
    idCardPhotos?: Array<{ role?: string; dataUrl?: string; capturedAt?: string }> | null;
  }>;
}

export interface PiercingAppointment {
  id: string;
  clientId: string;
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  clientBirthDate?: string;
  shopName: string;
  serviceName: string;
  serviceCategory: string;
  scheduledAt: string;
  estimatedEndTime: string;
  status: string;
  notes?: string;
  pricing: {
    basePrice?: number;
    depositAmount?: number;
    [key: string]: any;
  };
  bookingSource?: string;
}

interface PiercingWorkflowState {
  appointment: PiercingAppointment | null;
  acts: PiercingAct[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  updateActLocal: (actId: string, updates: Partial<PiercingAct>) => void;
  /** Ajoute un acte renvoyé par POST …/appointments/:id/acts (affichage immédiat avant fin du reload). */
  appendActLocal: (act: PiercingAct) => void;
  removeActLocal: (actId: string) => void;
  workflowSupplements: WorkflowSupplement[];
  setWorkflowSupplements: React.Dispatch<React.SetStateAction<WorkflowSupplement[]>>;
}

const PiercingWorkflowContext = createContext<PiercingWorkflowState | null>(null);

export function PiercingWorkflowProvider({ children }: { children: ReactNode }) {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const [appointment, setAppointment] = useState<PiercingAppointment | null>(null);
  const [acts, setActs] = useState<PiercingAct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workflowSupplements, setWorkflowSupplements] = useState<WorkflowSupplement[]>([]);
  const appointmentRef = useRef<PiercingAppointment | null>(null);
  appointmentRef.current = appointment;
  const lastFetchedAppointmentId = useRef<string | undefined>(undefined);

  const fetchDetails = useCallback(async () => {
    if (!appointmentId) return;
    if (lastFetchedAppointmentId.current !== appointmentId) {
      lastFetchedAppointmentId.current = appointmentId;
      setWorkflowSupplements([]);
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient(`/api/practitioner/appointments/${appointmentId}/details`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Erreur de chargement');
      }
      const data = await res.json();
      const appt = data.data.appointment as PiercingAppointment;
      setAppointment(appt);
      setWorkflowSupplements(normalizeWorkflowSupplements(appt.pricing?.workflowSupplements));
      setActs(data.data.acts);
    } catch (err: any) {
      setError(err.message || 'Impossible de charger le rendez-vous');
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const updateActLocal = useCallback((actId: string, updates: Partial<PiercingAct>) => {
    setActs((prev) => prev.map((a) => (a.id === actId ? { ...a, ...updates } : a)));
  }, []);

  const appendActLocal = useCallback((act: PiercingAct) => {
    setActs((prev) => {
      if (prev.some((a) => a.id === act.id)) return prev;
      return [...prev, act].sort((a, b) => a.sequence - b.sequence);
    });
  }, []);

  const removeActLocal = useCallback((actId: string) => {
    setActs((prev) => {
      const filtered = prev.filter((a) => a.id !== actId);
      return filtered.map((act, index) => ({ ...act, sequence: index + 1 }));
    });
  }, []);

  // Persister les suppléments dans pricing (merge) pour survivre au refresh et rester aligné serveur
  useEffect(() => {
    if (!appointmentId || loading) return;
    const ap = appointmentRef.current;
    if (!ap) return;
    const serverSig = supplementsSignature(normalizeWorkflowSupplements(ap.pricing?.workflowSupplements));
    if (serverSig === supplementsSignature(workflowSupplements)) return;

    const t = window.setTimeout(() => {
      void (async () => {
        const latest = appointmentRef.current;
        if (!latest) return;
        const merged = {
          ...(latest.pricing ?? {}),
          workflowSupplements: workflowSupplements.map((s) => ({
            id: s.id,
            productId: s.productId,
            productCode: s.productCode,
            piercingTypeId: s.piercingTypeId,
            name: s.name,
            price: s.price,
            qty: s.qty,
          })),
        };
        try {
          const res = await apiClient(`/api/practitioner/appointments/${appointmentId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pricing: merged }),
          });
          if (res.ok) {
            setAppointment((prev) => (prev ? { ...prev, pricing: merged as PiercingAppointment['pricing'] } : null));
          }
        } catch {
          /* éviter de bloquer l’UI ; prochain reload resync */
        }
      })();
    }, 450);
    return () => window.clearTimeout(t);
  }, [workflowSupplements, appointmentId, loading]);

  return (
    <PiercingWorkflowContext.Provider
      value={{
        appointment,
        acts,
        loading,
        error,
        reload: fetchDetails,
        updateActLocal,
        appendActLocal,
        removeActLocal,
        workflowSupplements,
        setWorkflowSupplements,
      }}
    >
      {children}
    </PiercingWorkflowContext.Provider>
  );
}

export function usePiercingWorkflow(): PiercingWorkflowState {
  const ctx = useContext(PiercingWorkflowContext);
  if (!ctx) throw new Error('usePiercingWorkflow must be used within PiercingWorkflowProvider');
  return ctx;
}
